import { Tab, Tabs, TabsList } from "@mui/base";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { SyntheticEvent, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { SiteUpdateAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import Button from "~/components/mantine/button/Button";
import { AiVisibilityOptimizationsRHFAdapter } from "~/components/siteSettings/AiVisibilityOptimizationsRHFAdapter";
import type { SiteSettingsFormValues } from "~/components/siteSettings/SiteSettings";
import styles from "~/components/siteSettings/SiteSettings.module.scss";
import WafSettings from "~/components/siteSettings/WafSettings";
import Breadcrumbs from "~/components/ui/breadcrumbs/Breadcrumbs";
import Spinner from "~/components/ui/spinner/Spinner";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";
import {
  FEATURE_FLAGS,
  isFeatureFlagEnabled,
  useFeatureFlagsConfig,
} from "~/featureFlags";
import { usePageTitle } from "~/hooks/usePageTitle";
import { useSite } from "~/hooks/useSite";
import { routes } from "~/router";

import pageStyles from "../siteSettings/SiteSettingsPage.module.scss";

// The fields this page is allowed to mutate. `site_speed_requested` is
// intentionally excluded — it isn't a plain settings field and is handled
// separately in `onSubmit` via the `requestSiteSpeed` mutation. The `waf_*`
// fields are edited on the Firewall tab (moved here from the Site Settings
// module) and persist through the same `sitesPartialUpdate` call.
const EDITABLE_FIELDS = [
  "prerendering",
  "apex_redirect",
  "inject_widget",
  "inject_markdown",
  "disable_defer_automation",
  "disable_lazy_automation",
  "disable_preload_automation",
  "waf_enabled",
  "waf_level",
  "waf_exclusions",
] as const;

// Array-valued fields need content comparison — identical values with
// different references would otherwise read as "changed".
const ARRAY_FIELDS = new Set<keyof SiteSettingsFormValues>(["waf_exclusions"]);

const VisibilityEngineSettingsPage = () => {
  usePageTitle(PAGE_TITLES.aiVisibilityEngine);

  const { id } = useParams();

  if (!id) {
    throw new Error("No site id provided");
  }

  const location = useLocation();
  const navigate = useNavigate();
  const flags = useFeatureFlagsConfig();
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const { data: siteData, isLoading } = useSite(id);

  const methods = useForm<SiteSettingsFormValues>();
  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    const site = siteData?.site;

    if (!site) return;

    methods.reset({
      site_speed_requested: site.site_speed_requested,
      prerendering: site.prerendering,
      apex_redirect: !!site.apex_redirect,
      inject_widget: !!site.inject_widget,
      inject_markdown: site.inject_markdown,
      disable_defer_automation: site.disable_defer_automation,
      disable_lazy_automation: site.disable_lazy_automation,
      disable_preload_automation: site.disable_preload_automation,
      waf_enabled: site.waf_enabled,
      waf_level: site.waf_level,
      waf_exclusions: site.waf_exclusions,
    });
  }, [siteData?.site, methods]);

  const handleSuccess = (message: string) => {
    enqueueSnackbar(message, { variant: "success" });
    queryClient.invalidateQueries({ queryKey: queryKeys.siteDetails(id) });
  };

  const handleError = (message: string) => {
    enqueueSnackbar(message, { variant: "error", persist: true });
  };

  const { mutate: updateSite, isPending: updateSiteIsPending } = useMutation({
    mutationFn: async (data: SiteUpdateAttributes) =>
      apiClient.sites.sitesPartialUpdate(Number(siteData?.site.id), data),
    //@ts-expect-error BE types
    onError: ({ error }) => {
      const errorMessage =
        error?.error?.message || "Site settings are not updated!";
      handleError(errorMessage);
    },
    onSuccess: () => handleSuccess("Site settings updated successfully!"),
  });

  const { mutate: requestSiteSpeed, isPending: requestSiteSpeedIsPending } =
    useMutation({
      mutationFn: async (siteId: number) =>
        apiClient.sites.siteSpeedApplyCreate(siteId),
      onError: () => handleError("Site speed status is not updated!"),
      onSuccess: () => handleSuccess("Site speed status updated successfully!"),
    });

  const extractChangedValues = () => {
    const formData = methods.getValues();
    const site = siteData?.site;
    const changedValues: Partial<SiteUpdateAttributes> = {};

    EDITABLE_FIELDS.forEach((key) => {
      const formValue = formData[key];
      const siteValue = site?.[key];

      const changed = ARRAY_FIELDS.has(key)
        ? JSON.stringify(formValue) !== JSON.stringify(siteValue)
        : formValue !== siteValue;

      if (changed) {
        //@ts-expect-error BE types
        changedValues[key] = formValue;
      }
    });

    return changedValues;
  };

  const onSubmit = (formData: SiteSettingsFormValues) => {
    const site = siteData?.site;

    // Only submit an application when the user turns the request on (the
    // backend defaults site_speed_status to "pending", so it can't be used to
    // detect this — site_speed_requested is the real signal).
    if (formData.site_speed_requested && !site?.site_speed_requested) {
      requestSiteSpeed(site?.id ?? 0);
    }

    const changedValues = extractChangedValues();

    if (Object.keys(changedValues).length > 0) {
      updateSite(changedValues as SiteUpdateAttributes);
    }
  };

  const handleReset = () => {
    const site = siteData?.site;

    methods.reset({
      site_speed_requested: site?.site_speed_requested,
      prerendering: site?.prerendering,
      apex_redirect: !!site?.apex_redirect,
      inject_widget: !!site?.inject_widget,
      inject_markdown: site?.inject_markdown,
      disable_defer_automation: site?.disable_defer_automation,
      disable_lazy_automation: site?.disable_lazy_automation,
      disable_preload_automation: site?.disable_preload_automation,
      waf_enabled: site?.waf_enabled,
      waf_level: site?.waf_level,
      waf_exclusions: site?.waf_exclusions,
    });
  };

  // The Firewall runs at the edge of the AI Visibility Engine proxy, so it's
  // only meaningful for teams whose plan includes the engine and for sites
  // whose application wasn't rejected (a rejected site can never enable the
  // proxy). Mirror the gate used by the rest of the engine (sidebar / Traffic
  // page) so the Firewall tab tracks the same entitlement the old WAF tab did.
  const aiVisibilityEngineAvailable =
    !!team?.subscription?.price?.product?.prerendering &&
    siteData?.site?.site_speed_status !== "rejected";

  const rawTabs = useMemo(
    () => [
      {
        id: "settings",
        value: 0,
        label: "AI Visibility Engine",
        title: "AI Visibility Engine Settings",
        content: () => (
          <AiVisibilityOptimizationsRHFAdapter
            siteData={siteData}
            isLoading={isLoading}
          />
        ),
      },
      ...(aiVisibilityEngineAvailable
        ? [
            {
              id: "firewall",
              value: 1,
              label: "Firewall",
              title: "Web Application Firewall Settings",
              feature: FEATURE_FLAGS.SITES_SETTINGS_WAF,
              content: () => (
                <WafSettings siteData={siteData} isLoading={isLoading} />
              ),
            },
          ]
        : []),
    ],
    [siteData, isLoading, aiVisibilityEngineAvailable],
  );

  const tabs = useMemo(() => {
    if (!flags) return rawTabs;

    return rawTabs.filter((tab) => {
      const feature = "feature" in tab ? tab.feature : undefined;

      return !feature || isFeatureFlagEnabled(flags, feature);
    });
  }, [rawTabs, flags]);

  const currentTabId = location.pathname.split("/").pop();
  const activeTab =
    tabs.find((tab) => tab.id === currentTabId)?.value ?? tabs[0]?.value ?? 0;
  const currentTab = tabs.find((tab) => tab.id === currentTabId) ?? tabs[0];

  const handleTabChange = (
    _event: SyntheticEvent | null,
    newValue: string | number | null,
  ) => {
    const selectedTab = tabs.find((tab) => tab.value === newValue);

    if (!selectedTab) return;

    if (selectedTab.id === "settings") {
      navigate(routes.site.visibilityEngineSettings({ id }));
    } else {
      navigate(
        routes.site.visibilityEngineSettingsTab({ id, tabId: selectedTab.id }),
      );
    }
  };

  const isPending = updateSiteIsPending || requestSiteSpeedIsPending;

  const fakeBreadcrumbs = [
    {
      title: PAGE_TITLES.dashboard,
      link: routes.home(),
    },
    {
      title: PAGE_TITLES.aiVisibilityEngine,
      link: routes.site.visibilityEngineSettings({ id }),
    },
  ];

  return (
    <div className={pageStyles.page}>
      <h1 className={pageStyles.title}>{PAGE_TITLES.aiVisibilityEngine}</h1>
      <Breadcrumbs fakeBreadcrumbs={fakeBreadcrumbs} />

      <main className={styles.settings}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <div className={styles.settingsContent}>
                <nav className={styles.settingsTabs}>
                  <TabsList className={styles.settingsTabsList}>
                    {tabs.map((tab) => (
                      <Tab
                        key={tab.id}
                        className={styles.settingsTab}
                        value={tab.value}
                      >
                        {tab.label}
                      </Tab>
                    ))}
                  </TabsList>
                </nav>

                {currentTab && (
                  <div className={styles.settingsPanel}>
                    <section className={styles.settingsSection}>
                      <h2 className={styles.settingsSectionTitle}>
                        {currentTab.title}
                      </h2>
                    </section>
                    {currentTab.content()}
                  </div>
                )}
              </div>
            </Tabs>

            <div className={styles.settingsFooter}>
              <div className={styles.settingsFooterButtons}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleReset}
                  disabled={!isDirty}
                >
                  Revert
                </Button>
                <Button type="submit" disabled={!isDirty || isPending}>
                  {isPending && <Spinner size="1rem" />}
                  Save Changes
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </main>
    </div>
  );
};

export default VisibilityEngineSettingsPage;
